import { useEffect, useRef } from 'react';
import { gsap } from '../lib/gsapConfig';

const TrustpilotLogo = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#00b67a" />
    <path
      d="M16 6.5l2.34 7.2h7.56l-6.12 4.44 2.34 7.2L16 20.9l-6.12 4.44 2.34-7.2L6.1 13.7h7.56L16 6.5z"
      fill="white"
    />
  </svg>
);

const GoogleLogo = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="white" />
    <path
      d="M28.6 16.32c0-.9-.08-1.76-.22-2.6H16v4.92h7.1a6.07 6.07 0 01-2.63 3.98v3.3h4.26c2.5-2.3 3.93-5.7 3.93-9.6z"
      fill="#4285F4"
    />
    <path
      d="M16 29c3.56 0 6.55-1.18 8.73-3.18l-4.26-3.3c-1.18.8-2.7 1.27-4.47 1.27-3.44 0-6.35-2.32-7.39-5.44H4.2v3.41A13 13 0 0016 29z"
      fill="#34A853"
    />
    <path
      d="M8.61 18.35A7.82 7.82 0 018.2 16c0-.81.14-1.6.41-2.35V10.24H4.2A13 13 0 003 16c0 2.1.5 4.08 1.2 5.76l4.41-3.41z"
      fill="#FBBC05"
    />
    <path
      d="M16 8.21c1.94 0 3.68.67 5.05 1.97l3.78-3.78C22.54 4.18 19.55 3 16 3A13 13 0 004.2 10.24l4.4 3.41C9.65 10.53 12.56 8.21 16 8.21z"
      fill="#EA4335"
    />
  </svg>
);

const StarRow = ({ count = 5, color = '#EB9813', size = 14 }) => (
  <div className="flex gap-0.5" aria-hidden="true">
    {[...Array(count)].map((_, i) => (
      <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ))}
  </div>
);

const badges = [
  {
    logo: <TrustpilotLogo />,
    platform: 'Trustpilot',
    platformColor: 'text-[#00b67a]',
    value: '4.8 / 5 · Excellent',
    detail: 'Based on 1,200+ verified reviews',
    stars: <StarRow color="#00b67a" />,
  },
  {
    logo: <GoogleLogo />,
    platform: 'Google Reviews',
    platformColor: 'text-[#4285F4]',
    value: '4.9 Stars',
    detail: 'Rated by customers across the US',
    stars: <StarRow color="#FBBC05" />,
  },
  {
    logo: (
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-50 border border-green-200">
        <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M9 12l2 2 4-4m5-4v5c0 5.25-3.5 10.25-8 11-4.5-.75-8-5.75-8-11V6l8-3 8 3z" />
        </svg>
      </div>
    ),
    platform: 'USDOT Verified',
    platformColor: 'text-green-700',
    value: '500+ Movers',
    detail: 'License & insurance reviewed',
    stars: null,
  },
  {
    logo: (
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-200">
        <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6M8 10h.01M12 10h.01M16 10h.01" />
        </svg>
      </div>
    ),
    platform: 'Proven Track Record',
    platformColor: 'text-blue-700',
    value: '10,000+ Moves',
    detail: 'Completed by network movers',
    stars: null,
  },
];

export default function TrustBar() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.trust-card', {
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 85%',
        },
        y: 26,
        opacity: 0,
        duration: 0.55,
        stagger: 0.08,
        ease: 'power3.out',
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative z-20 bg-white px-4 py-5">
      <div className="mx-auto max-w-7xl rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {badges.map((badge) => (
            <div key={badge.platform} className="trust-card rounded-xl border border-slate-100 bg-slate-50/80 p-4">
              {/* Header: logo + platform name */}
              <div className="mb-3 flex items-center gap-2.5">
                {badge.logo}
                <span className={`text-sm font-extrabold ${badge.platformColor}`}>
                  {badge.platform}
                </span>
              </div>

              {/* Stars (if applicable) */}
              {badge.stars && <div className="mb-2">{badge.stars}</div>}

              {/* Rating / value */}
              <div className="text-lg font-extrabold text-slate-950">{badge.value}</div>

              {/* Detail */}
              <p className="mt-1 text-sm leading-5 text-slate-500">{badge.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
